import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useTheme,
  alpha,
  useMediaQuery,
} from '@mui/material';
import {
  AutoAwesome,
  People,
  Groups,
  EmojiEvents,
  Settings,
  SportsBaseball,
  MenuBook,
  Whatshot,
  Casino,
  SwapHoriz,
  ShowChart,
  WorkspacePremium,
  Speed,
  AdminPanelSettings,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ open, onClose, variant = 'temporary' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const navigationItems = [
    {
      section: 'Main',
      items: [
        {
          text: 'Cycle IQ',
          icon: <AutoAwesome />,
          path: '/',
          description: 'AI search — ask anything about baseball'
        },
        {
          text: 'Players',
          icon: <People />,
          path: '/players',
          description: 'Stats, profiles & analysis'
        },
        {
          text: 'Teams',
          icon: <Groups />,
          path: '/teams',
          description: 'Rosters, performance & depth'
        },
        {
          text: 'Standings',
          icon: <EmojiEvents />,
          path: '/standings',
          description: 'League standings & records'
        },
        {
          text: 'Scores',
          icon: <SportsBaseball />,
          path: '/scores',
          description: 'Live & historical scores'
        }
      ]
    },
    {
      section: 'Explore',
      items: [
        {
          text: 'Pitch Lab',
          icon: <Speed />,
          path: '/pitch-lab',
          description: 'Pitch type, velocity & movement analysis'
        },
        {
          text: 'At-Bat Explorer',
          icon: <SportsBaseball />,
          path: '/at-bat-explorer',
          description: 'Pitch-by-pitch at-bat viewer'
        },
        {
          text: 'Awards Tracker',
          icon: <EmojiEvents />,
          path: '/awards',
          description: 'MVP, Cy Young & ROY races'
        }
      ]
    },
    {
      section: 'Pro',
      pro: true,
      items: [
        {
          text: 'Fantasy Value Board',
          icon: <Casino />,
          path: '/fantasy',
          description: 'CVR × salary efficiency — tonight\'s best starts'
        },
        {
          text: 'Trade Value Engine',
          icon: <SwapHoriz />,
          path: '/trades',
          description: 'CVR-powered trade analysis'
        },
        {
          text: 'Momentum Board',
          icon: <ShowChart />,
          path: '/momentum',
          description: '30-day CVR momentum — risers, fallers & breakouts'
        },
      ]
    },
    {
      section: 'Tools',
      items: [
        {
          text: 'Glossary',
          icon: <MenuBook />,
          path: '/glossary',
          description: 'Stat definitions & formulas'
        },
        {
          text: 'Settings',
          icon: <Settings />,
          path: '/settings',
          description: 'App preferences'
        },
        ...(user?.isAdmin ? [{
          text: 'Admin',
          icon: <AdminPanelSettings />,
          path: '/admin',
          description: 'User management & analytics'
        }] : [])
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (variant === 'temporary') {
      onClose();
    }
  };

  const isCurrentPath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const drawerWidth = isMobile ? 260 : 280;

  const drawerContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: '100%',
        backgroundColor: 'background.paper',
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pt: { xs: 2, md: 3 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              color: 'white'
            }}
          >
            <SportsBaseball sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
              The Cycle
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MLB Analytics
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {navigationItems.map((section, sectionIndex) => (
          <Box key={section.section} sx={{ mb: 2 }}>
            {section.pro ? (
              <Box sx={{
                mx: 2, mb: 0.5, mt: 1, px: 1.5, py: 0.75, borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)}, ${alpha(theme.palette.warning.dark || theme.palette.warning.main, 0.08)})`,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                display: 'flex', alignItems: 'center', gap: 0.75,
              }}>
                <WorkspacePremium sx={{ fontSize: 14, color: 'warning.main' }} />
                <Typography variant="overline" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'warning.main', letterSpacing: '0.1em', lineHeight: 1 }}>
                  Pro Features
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="overline"
                sx={{ px: 2, py: 1, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', letterSpacing: '0.1em' }}
              >
                {section.section}
              </Typography>
            )}
            
            <List dense sx={{ px: 1 }}>
              {section.items.map((item, itemIndex) => {
                const isActive = isCurrentPath(item.path);
                const isPermanent = variant === 'permanent';
                const isPro = section.pro;
                
                const itemContent = (
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          borderRadius: 2,
                          mx: 1,
                          backgroundColor: isActive 
                            ? alpha(isPro ? theme.palette.warning.main : theme.palette.primary.main, 0.1)
                            : 'transparent',
                          color: isActive 
                            ? (isPro ? 'warning.main' : theme.palette.primary.main)
                            : 'text.primary',
                          '&:hover': {
                            backgroundColor: isActive
                              ? alpha(isPro ? theme.palette.warning.main : theme.palette.primary.main, 0.15)
                              : alpha(isPro ? theme.palette.warning.main : theme.palette.action.hover, 0.08),
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          '&:active': {
                            transform: 'translateX(2px)'
                          },
                          py: isMobile ? 1.75 : 1.5,
                          px: 2,
                          minHeight: isMobile ? 48 : 'auto',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isActive ? 'inherit' : (isPro ? alpha(theme.palette.warning.main, 0.8) : 'inherit'),
                            minWidth: 40,
                            '& .MuiSvgIcon-root': {
                              fontSize: 22
                            }
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={isActive ? 600 : 500}
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {item.text}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                mt: 0.25,
                                display: 'block'
                              }}
                            >
                              {item.description}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                );
                
                if (isPermanent) {
                  return <React.Fragment key={item.path}>{itemContent}</React.Fragment>;
                }
                
                return (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: itemIndex * 0.02, duration: 0.15 }}
                  >
                    {itemContent}
                  </motion.div>
                );
              })}
            </List>
            
            {sectionIndex < navigationItems.length - 1 && (
              <Divider sx={{ mx: 2, my: 2, opacity: 0.6 }} />
            )}
          </Box>
        ))}
      </Box>

    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      PaperProps={{
        sx: {
          borderRight: 'none',
          boxShadow: variant === 'temporary' ? theme.shadows[8] : 'none'
        }
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box'
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
